import classnames from 'classnames'
import moment from 'moment'
import PropTypes from 'prop-types'
import { Component, createRef } from 'react'
import onClickOutside from 'react-onclickoutside'

import Calendar from './calendar'
import DateInput from './DateInput'
import {
  isDayDisabled,
  isDayInRange,
  getEffectiveMinDate,
  getEffectiveMaxDate
} from './DateUtils'
import TetherComponent from './TetherComponent'

import './styles.scss'

const outsideClickIgnoreClass = 'react-datepicker-ignore-onclickoutside'
const WrappedCalendar = onClickOutside(Calendar)

export class DatePicker extends Component {
  constructor (props) {
    super(props)

    const part1 =
      this.props.selectsStart && this.props.endDate
        ? moment(this.props.endDate)
        : moment()
    const part2 =
      this.props.selectsEnd && this.props.startDate
        ? moment(this.props.startDate)
        : part1
    const defaultPreSelection = this.props.openToDate
      ? moment(this.props.openToDate)
      : part2

    const minDate = getEffectiveMinDate(this.props)
    const maxDate = getEffectiveMaxDate(this.props)

    const boundedMax =
      maxDate && defaultPreSelection.isAfter(maxDate)
        ? maxDate
        : defaultPreSelection
    const boundedPreSelection =
      minDate && defaultPreSelection.isBefore(minDate) ? minDate : boundedMax

    this.state = {
      open: false,
      preventFocus: false,
      preSelection: this.props.selected
        ? moment(this.props.selected)
        : boundedPreSelection
    }

    this.input = createRef()
    this.onInputClick = this.onInputClick.bind(this)
    this.handleFocus = this.handleFocus.bind(this)
    this.setOpen = this.setOpen.bind(this)
    this.handleCalendarClickOutside =
      this.handleCalendarClickOutside.bind(this)
    this.handleBlur = this.handleBlur.bind(this)
    this.handleSelect = this.handleSelect.bind(this)
    this.setSelected = this.setSelected.bind(this)
    this.setPreSelection = this.setPreSelection.bind(this)
    this.validate = this.validate.bind(this)
    this.calendar = createRef()
  }

  componentWillUnmount () {
    this.clearPreventFocusTimeout()
  }

  handleFocus () {
    if (!this.state.preventFocus) {
      this.input.current?.focus()
    }
  }

  handleDropdownFocus () {
    this.cancelFocusInput()
  }

  handleBlur (event) {
    if (this.state.open) {
      this.deferFocusInput()
    } else {
      this.props.onBlur(event)
      this.validate()
    }
  }

  handleCalendarClickOutside (event) {
    this.setOpen(false)
    this.props.onClickOutside(event)
    if (this.props.withPortal) {
      event.preventDefault()
    }
  }

  handleSelect (date, event) {
    // Preventing onFocus event to fix issue
    // https://github.com/Hacker0x01/react-datepicker/issues/628
    this.setState({ preventFocus: true }, () => {
      this.preventFocusTimeout = setTimeout(
        () => this.setState({ preventFocus: false }),
        50
      )
      return this.preventFocusTimeout
    })
    this.setSelected(date, event)
    this.setOpen(false)
  }

  setSelected (date, event) {
    let changedDate = date

    if (changedDate !== null && isDayDisabled(changedDate, this.props)) {
      return
    }

    if (this.props.disableDateAutoCorrection) {
      if (changedDate !== null) {
        if (this.props.selected) {
          changedDate = moment(changedDate)
            .set({
              hour: this.props.selected.hour(),
              minute: this.props.selected.minute(),
              second: this.props.selected.second()
            })
            .format('MM/DD/YYYY')
        }
        this.setState({
          preSelection: changedDate
        })
      }

      this.props.onChange(changedDate, event)
    }

    this.props.onChange(changedDate, event)
  }

  setPreSelection (date) {
    const isDateRangePresent =
      typeof this.props.minDate !== 'undefined' &&
      typeof this.props.maxDate !== 'undefined'
    const isValidDateSelection =
      isDateRangePresent && date
        ? isDayInRange(date, this.props.minDate, this.props.maxDate)
        : true
    if (isValidDateSelection) {
      this.setState({
        preSelection: date
      })
    }
  }

  onInputClick () {
    if (!this.props.disabled) {
      this.setOpen(true)
    }
  }

  onInputKeyDown (event) {
    if (!this.state.open && !this.props.inline) {
      if (/^Arrow/.test(event.key)) {
        this.onInputClick()
      }
      return
    }
    const copy = moment(this.state.preSelection)
    if (event.key === 'Enter') {
      event.preventDefault()
      this.handleSelect(copy, event)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.setOpen(false)
    } else if (event.key === 'Tab') {
      this.setOpen(false)
    }
    if (!this.props.disabledKeyboardNavigation) {
      let newSelection
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          newSelection = copy.subtract(1, 'days')
          break
        case 'ArrowRight':
          event.preventDefault()
          newSelection = copy.add(1, 'days')
          break
        case 'ArrowUp':
          event.preventDefault()
          newSelection = copy.subtract(1, 'weeks')
          break
        case 'ArrowDown':
          event.preventDefault()
          newSelection = copy.add(1, 'weeks')
          break
        case 'PageUp':
          event.preventDefault()
          newSelection = copy.subtract(1, 'months')
          break
        case 'PageDown':
          event.preventDefault()
          newSelection = copy.add(1, 'months')
          break
        case 'Home':
          event.preventDefault()
          newSelection = copy.subtract(1, 'years')
          break
        case 'End':
          event.preventDefault()
          newSelection = copy.add(1, 'years')
          break
        default:
          break
      }
      this.setPreSelection(newSelection)
    }
  }

  onClearClick (event) {
    event.preventDefault()
    this.props.onChange(null, event)
  }

  setOpen (open) {
    this.setState(s => ({
      open,
      preSelection: open && s.open ? s.preSelection : s.preSelection
    }))
  }

  setFocus () {
    this.input.current?.focus()
  }

  clearPreventFocusTimeout () {
    if (this.preventFocusTimeout) {
      clearTimeout(this.preventFocusTimeout)
    }
  }

  deferFocusInput () {
    this.cancelFocusInput()
    this.inputFocusTimeout = window.setTimeout(() => this.setFocus(), 1)
  }

  cancelFocusInput () {
    clearTimeout(this.inputFocusTimeout)
    this.inputFocusTimeout = null
  }

  renderCalendar () {
    if (!this.props.inline && (!this.state.open || this.props.disabled)) {
      return null
    }
    return (
      <WrappedCalendar
        ref={this.calendar}
        locale={this.props.locale}
        dateFormat={this.props.dateFormatCalendar}
        dropdownMode={this.props.dropdownMode}
        selected={this.props.selected}
        preSelection={this.state.preSelection}
        onSelect={this.handleSelect}
        openToDate={this.props.openToDate}
        minDate={this.props.minDate}
        maxDate={this.props.maxDate}
        selectsStart={this.props.selectsStart}
        selectsEnd={this.props.selectsEnd}
        startDate={this.props.startDate}
        endDate={this.props.endDate}
        excludeDates={this.props.excludeDates}
        filterDate={this.props.filterDate}
        onClickOutside={this.handleCalendarClickOutside}
        highlightDates={this.props.highlightDates}
        includeDates={this.props.includeDates}
        inline={this.props.inline}
        peekNextMonth={this.props.peekNextMonth}
        showMonthDropdown={this.props.showMonthDropdown}
        showWeekNumbers={this.props.showWeekNumbers}
        showYearDropdown={this.props.showYearDropdown}
        forceShowMonthNavigation={this.props.forceShowMonthNavigation}
        scrollableYearDropdown={this.props.scrollableYearDropdown}
        todayButton={this.props.todayButton}
        utcOffset={this.props.utcOffset}
        outsideClickIgnoreClass={outsideClickIgnoreClass}
        fixedHeight={this.props.fixedHeight}
        monthsShown={this.props.monthsShown}
        onDropdownFocus={this.handleDropdownFocus}
        onMonthChange={this.props.onMonthChange}
        className={this.props.calendarClassName}
      >
        {this.props.children}
      </WrappedCalendar>
    )
  }

  renderDateInput () {
    const className = classnames(this.props.className, {
      [outsideClickIgnoreClass]: this.state.open
    })
    return (
      <DateInput
        ref={this.input}
        disableDateAutoCorrection={this.props.disableDateAutoCorrection}
        id={this.props.id}
        name={this.props.name}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={this.props.autoFocus}
        date={this.props.selected}
        locale={this.props.locale}
        minDate={this.props.minDate}
        maxDate={this.props.maxDate}
        excludeDates={this.props.excludeDates}
        includeDates={this.props.includeDates}
        filterDate={this.props.filterDate}
        dateFormat={this.props.dateFormat}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onCalenderOpen={this.onInputClick}
        onChangeRaw={this.props.onChange}
        onChangeDate={this.setSelected}
        placeholder={this.props.placeholderText}
        disabled={this.props.disabled}
        autoComplete={this.props.autoComplete}
        className={className}
        title={this.props.title}
        readOnly={this.props.readOnly}
        required={this.props.required}
        tabIndex={this.props.tabIndex}
        customInput={this.props.customInput}
        label={this.props.label}
        value={this.props.value}
        error={this.state.error}
      />
    )
  }

  renderClearButton () {
    if (this.props.isClearable && this.props.selected !== null) {
      return (
        <button
          aria-hidden='true'
          className='react-datepicker__close-icon'
          href='#'
          onClick={this.onClearClick}
        />
      )
    }
    return null
  }

  validate () {
    const { value, maxDate, onChange } = this.props
    const max = maxDate ? maxDate.format('MM/DD/YYYY') : undefined
    if (value && max && moment(value).isAfter(max)) {
      this.setState({
        error: 'Date is after maximum date'
      })
      onChange(undefined)
    }
    if (moment(value).format() === 'Invalid date') {
      this.setState({
        error: 'Date is Invalid'
      })
      onChange(undefined)
    }
    if (moment(value).format() !== 'Invalid date') {
      this.setState({
        error: undefined
      })
    }
  }

  render () {
    const calendar = this.renderCalendar()

    if (this.props.inline && !this.props.withPortal) {
      return calendar
    }

    if (this.props.withPortal) {
      return (
        <div>
          {!this.props.inline
            ? (
              <div className='react-datepicker__input-container'>
                {this.renderDateInput()}
                {this.renderClearButton()}
              </div>
            )
            : null}
          {this.state.open || this.props.inline
            ? (
              <div className='react-datepicker__portal'>{calendar}</div>
            )
            : null}
        </div>
      )
    }

    return (
      <TetherComponent
        classPrefix='react-datepicker__tether'
        attachment={this.props.popoverAttachment}
        targetAttachment={this.props.popoverTargetAttachment}
        targetOffset={this.props.popoverTargetOffset}
        renderElementTo={this.props.renderCalendarTo}
        constraints={this.props.tetherConstraints}
      >
        <div className='react-datepicker__input-container'>
          {this.renderDateInput()}
          {this.renderClearButton()}
        </div>
        {calendar}
      </TetherComponent>
    )
  }
}

DatePicker.displayName = 'DatePicker'

DatePicker.defaultProps = {
  dateFormatCalendar: 'MMMM YYYY',

  disabled: false,
  disableDateAutoCorrection: false,
  disabledKeyboardNavigation: false,
  dropdownMode: 'scroll',

  popoverAttachment: 'top left',
  popoverTargetAttachment: 'bottom left',
  popoverTargetOffset: '10px 0',
  tetherConstraints: [
    {
      to: 'window',
      attachment: 'together'
    }
  ],
  utcOffset: moment().utcOffset(),
  monthsShown: 1,
  withPortal: false
}

DatePicker.propTypes = {
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  calendarClassName: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  customInput: PropTypes.element,
  dateFormat: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Array)
  ]),
  dateFormatCalendar: PropTypes.string,
  disabled: PropTypes.bool,
  disableDateAutoCorrection: PropTypes.bool,
  disabledKeyboardNavigation: PropTypes.bool,
  dropdownMode: PropTypes.oneOf([ 'scroll', 'select' ]),
  endDate: PropTypes.instanceOf(Object),
  excludeDates: PropTypes.instanceOf(Array),
  filterDate: PropTypes.func,
  fixedHeight: PropTypes.bool,
  highlightDates: PropTypes.instanceOf(Array),
  id: PropTypes.string,
  includeDates: PropTypes.instanceOf(Array),
  inline: PropTypes.bool,
  isClearable: PropTypes.bool,
  locale: PropTypes.string,
  label: PropTypes.string.isRequired,
  maxDate: PropTypes.instanceOf(Object),
  minDate: PropTypes.instanceOf(Object),
  monthsShown: PropTypes.number,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onClickOutside: PropTypes.func,
  onMonthChange: PropTypes.func,
  openToDate: PropTypes.instanceOf(Object),
  peekNextMonth: PropTypes.bool,
  placeholderText: PropTypes.string,
  popoverAttachment: PropTypes.string,
  popoverTargetAttachment: PropTypes.string,
  popoverTargetOffset: PropTypes.string,
  readOnly: PropTypes.bool,
  renderCalendarTo: PropTypes.any,
  required: PropTypes.bool,
  scrollableYearDropdown: PropTypes.bool,
  selected: PropTypes.instanceOf(Object),
  selectsEnd: PropTypes.bool,
  selectsStart: PropTypes.bool,
  showMonthDropdown: PropTypes.bool,
  showWeekNumbers: PropTypes.bool,
  showYearDropdown: PropTypes.bool,
  forceShowMonthNavigation: PropTypes.bool,
  startDate: PropTypes.instanceOf(Object),
  tabIndex: PropTypes.number,
  tetherConstraints: PropTypes.instanceOf(Array),
  title: PropTypes.string,
  todayButton: PropTypes.string,
  utcOffset: PropTypes.number,
  withPortal: PropTypes.bool,
  value: PropTypes.string
}

export default DatePicker
